<template>
    <div class="row q-col-gutter-md">
        <div
            class="col-sm-6 col-md-4 col-xs-12"
            v-for="(card, index) in getCards"
            :key="index"
        >
            <q-card
                @click="$router.push(card.route)"
                class="q-pa-md bg-accent card"
                :id="card.id"
            >
                <q-card-section class="text-center">
                    <q-icon :name="card.icon" color="white" size="xl"></q-icon>
                </q-card-section>
                <q-card-section>
                    <div
                        class="text-weight-bold text-center card__label ellipsis"
                    >
                        {{ card.label }}
                    </div>
                </q-card-section>
            </q-card>
        </div>
    </div>
</template>

<script>
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import { mapState } from 'pinia';
import { useSettingsStore } from '@stores/settings.js';

export default {
    name: 'FacilitySettings',
    mixins: [BreadcrumbsManager],
    props: {
        facilityId: {
            type: [Number, String],
            required: true
        }
    },
    mounted() {
        // set breadcrumbs
        this.setBreadcrumbs([
            {
                label: this.$t('Facilities'),
                route: {
                    name: 'facility_list'
                }
            },
            {
                label: this.facility.name,
                route: {
                    name: 'facility_dashboard',
                    params: {
                        facilityId: this.facility.id
                    }
                }
            },
            {
                label: this.$t('Settings')
            }
        ]);
    },
    data() {
        return {
            cards: [
                {
                    label: this.$t('Facility Details'),
                    icon: 'fas fa-edit',
                    route: {
                        name: 'facility_detail',
                        params: {
                            facilityId: this.facilityId
                        }
                    },
                    id: 'facility-detail'
                },
                {
                    label: this.$t('Causes'),
                    icon: 'fas fa-exclamation-circle',
                    route: {
                        name: 'cause_list',
                        params: {
                            facilityId: this.facilityId
                        }
                    },
                    id: 'cause-list'
                },
                {
                    label: this.$t('Products'),
                    icon: 'fas fa-box',
                    route: {
                        name: 'product_list',
                        params: {
                            facilityId: this.facilityId
                        }
                    },
                    id: 'product-list'
                },
                {
                    label: this.$t('Shift Management'),
                    icon: 'fas fa-calendar-alt',
                    route: {
                        name: 'shift_list'
                    },
                    id: 'shift-list'
                },
                {
                    label: this.$t('Groups'),
                    icon: 'fas fa-layer-group',
                    route: {
                        name: 'group_list'
                    },
                    id: 'group-list'
                },
                {
                    label: this.$t('Device Management'),
                    icon: 'fa-solid fa-router',
                    route: {
                        name: 'device_management'
                    },
                    id: 'device-management'
                }
            ],
            qualityDefectsCard: {
                label: this.$t('Quality Defects'),
                icon: 'fa-solid fa-clipboard-list-check',
                route: {
                    name: 'quality_defect_list',
                    params: {
                        facilityId: this.facilityId
                    }
                },
                id: 'quality-defect-list'
            }
        };
    },
    computed: {
        ...mapState(useSettingsStore, ['isTrackingQualityEvents']),
        /**
         * Get selected facility.
         * @return {Object} - Facility.
         */
        facility() {
            return this.$store.getters['facilities/getFacility'](
                this.facilityId
            );
        },
        /**
         * Retrieves the list of cards to be displayed.
         * If quality tracking is enabled, the quality defects card is inserted
         * into the list at the position before the card with the id 'shift-list'.
         *
         * @returns {Array} The array of cards to be displayed.
         */
        getCards() {
            if (!this.isTrackingQualityEvents) {
                return this.cards;
            }

            const cards = [...this.cards];
            const insertIndex = cards.findIndex(
                card => card.id === 'shift-list'
            );

            cards.splice(insertIndex, 0, this.qualityDefectsCard);

            return cards;
        }
    }
};
</script>

<style lang="scss" scoped>
.card {
    cursor: pointer;
}

.card__label {
    color: white;
}
</style>
